// File: src/pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; 
import '../css/HomePage.css';
import UpgradeRequestModal from '../components/UpgradeRequestModal';

// --- ICONS ---
const IconSearch = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const IconArrowRight = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>;
const IconMapPin = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
const IconBriefcase = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>;

// Helper Parse JWT
const parseJwt = (token) => {
    try {
      if (!token) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) { return null; }
};

// Badges
const HotBadge = () => <span className="inline-flex items-center gap-1 bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded border border-red-200 animate-pulse">HOT <span className="material-symbols-outlined text-[10px]">bolt</span></span>;
const TopBadge = () => <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 text-[10px] font-bold px-2 py-0.5 rounded border border-yellow-200">TOP <span className="material-symbols-outlined text-[10px]">star</span></span>;

// --- DATA MOCK CHO BANNER SLIDE (Enterprise Ads) ---
const ENTERPRISE_BANNERS = [
    {
        id: 1,
        image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
        company: "FPT Software",
        title: "Tuyển dụng Tech Lead 2026",
        desc: "Cơ hội onsite Nhật Bản, Mỹ. Lương up to $3000.",
        color: "from-blue-900"
    },
    {
        id: 2,
        image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
        company: "VNG Corporation",
        title: "Senior Product Manager",
        desc: "Kiến tạo sản phẩm triệu người dùng. Chế độ đãi ngộ hấp dẫn.",
        color: "from-purple-900"
    },
    {
        id: 3,
        image: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
        company: "Viettel Group",
        title: "Kỹ sư AI/Big Data",
        desc: "Tham gia các dự án trọng điểm quốc gia.",
        color: "from-red-900"
    }
];

// Cấu hình giới hạn hiển thị
const LIMITS = { free: 2, pro: 10, premium: 9999 };

export default function HomePage() {
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const locationObj = useLocation();
  const [banners, setBanners] = useState([]);

  // --- STATE ---
  const [userRole, setUserRole] = useState('guest'); 
  const [loading, setLoading] = useState(true);

  // Data
  const [jobs, setJobs] = useState([]); 
  const [hotJobs, setHotJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]); 
  const [topCompanies, setTopCompanies] = useState([]);
  const [provinces, setProvinces] = useState([]); 

  // Slider State
  const [currentSlide, setCurrentSlide] = useState(0);

  // Search
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // User Usage State
  const [cvUsage, setCvUsage] = useState({ used: 0, limit: 2 });
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // --- 1. CHECK ROLE & INIT ---
  useEffect(() => {
    const token = localStorage.getItem('skillmatch_token');
    let role = 'guest';
    if (token) {
        const decoded = parseJwt(token);
        if (decoded?.user?.role) role = decoded.user.role;
        setIsLoggedIn(true);
    }
    setUserRole(role);
    setLoading(false);
  }, []);

  // --- SLIDER LOGIC ---
  useEffect(() => {
      // ✅ FIX: Nếu <= 1 banner thì không set interval (không trượt)
      if (banners.length < 2) return; 

      const slideInterval = setInterval(() => {
          setCurrentSlide((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(slideInterval);
  }, [banners]);

  // --- 2. FETCH DATA & USAGE ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('skillmatch_token');

        // Provinces
        const provRes = await fetch('https://provinces.open-api.vn/api/?depth=1');
        if (provRes.ok) setProvinces(await provRes.json());

        // Fetch User Usage (Nếu đã login)
        if (token) {
            const meRes = await fetch(`${apiBaseUrl}/api/auth/me`, { headers: { 'x-auth-token': token } });
            if (meRes.ok) {
                const user = await meRes.json();
                const plan = user.subscription?.plan || 'free';
                const used = user.usage?.cvReviews?.count || 0;
                const limit = LIMITS[plan] || 2;
                setCvUsage({ used, limit });
            }
        }

        if (userRole === 'business') {
            // Business Data
            if (token) {
                const myJobsRes = await fetch(`${apiBaseUrl}/api/jobs/my-jobs`, { headers: { 'x-auth-token': token } });
                if (myJobsRes.ok) {
                    const data = await myJobsRes.json();
                    setMyJobs(data.slice(0, 3));
                }
            }
        } else {
           const bannerRes = await fetch(`${apiBaseUrl}/api/jobs/featured-companies`);
            if (bannerRes.ok) {
                const data = await bannerRes.json();
                
                // Map dữ liệu CÔNG TY vào Banner
                const formattedBanners = data.map(comp => ({
                    id: comp._id,
                    // Ưu tiên Cover Image, fallback sang ảnh stock đẹp
                    image: comp.profile?.coverImage || "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
                    company: comp.profile?.companyName || "Doanh nghiệp hàng đầu",
                    // Slogan hoặc mô tả ngắn
                    desc: comp.profile?.description ? comp.profile.description.substring(0, 100) + "..." : "Môi trường làm việc chuyên nghiệp, chế độ đãi ngộ hấp dẫn.",
                    link: `/companies/${comp._id}`, // Link sang trang chi tiết công ty
                    logo: comp.profile?.logo,
                    // Màu gradient nền
                    color: "from-blue-900" 
                }));

                // Nếu không có enterprise nào -> Banner mặc định
                if (formattedBanners.length === 0) {
                    setBanners([{
                        id: 'default',
                        image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=80",
                        company: "SkillMatch Platform",
                        desc: "Nền tảng tuyển dụng thông minh số 1 Việt Nam",
                        link: "/jobs",
                        color: "from-indigo-900"
                    }]);
                } else {
                    setBanners(formattedBanners);
                }
            }

            // ✅ 2. FETCH JOBS (Cho Live Feed)
            const jobsRes = await fetch(`${apiBaseUrl}/api/jobs`);
            if (jobsRes.ok) {
                const data = await jobsRes.json();
                // ... (Logic map jobs giữ nguyên) ...
                const activeJobs = data.filter(j => j.status === 'Đang tuyển' || j.status === 'PUBLISHED')
                    .map(j => ({
                        ...j,
                        isHot: (j.salary && (j.salary.includes('20') || j.salary.includes('30') || j.salary.includes('$'))) || false
                    }))
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                
                setHotJobs(activeJobs.slice(0, 10)); 
            }

            // ✅ 3. FETCH TOP COMPANIES (Cho Grid bên dưới)
            const compRes = await fetch(`${apiBaseUrl}/api/companies`);
            if (compRes.ok) {
                const compData = await compRes.json();
                setTopCompanies(compData.filter(c => c.jobCount > 0).map(c => ({...c, isTop: c.jobCount > 5})).sort((a, b) => b.jobCount - a.jobCount).slice(0, 6));
            }
        }
      } catch (error) { console.error("Lỗi tải data:", error); }
    };

    if (!loading) fetchData();
  }, [userRole, loading, apiBaseUrl]);

  // --- 3. LIVE FEED SCROLL EFFECT ---
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || hotJobs.length === 0) return;
    
    let scrollAmount = 0;
    const speed = 0.5;

    const step = () => {
        if (!scrollContainer) return;
        scrollAmount += speed;
        if (scrollAmount >= scrollContainer.scrollHeight / 2) {
            scrollAmount = 0; scrollContainer.scrollTop = 0;
        } else {
            scrollContainer.scrollTop = scrollAmount;
        }
        requestAnimationFrame(step);
    };
    const anim = requestAnimationFrame(step);
    return () => cancelAnimationFrame(anim);
  }, [hotJobs]);

  // --- HANDLERS ---
  const handleSearch = (e) => {
    e.preventDefault();
    if (userRole === 'business') {
        navigate(`/business/candidates?search=${encodeURIComponent(keyword)}`);
    } else {
        navigate(`/jobs?keyword=${encodeURIComponent(keyword)}&location=${encodeURIComponent(location)}`);
    }
  };

  const handleAIClick = () => {
    const token = localStorage.getItem('skillmatch_token');
    if (!token) {
        navigate('/login', { state: { from: '/' } });
        return;
    }
    fileInputRef.current.click();
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { alert("Vui lòng upload PDF"); return; }
    
    setIsUploading(true);
    const formData = new FormData(); formData.append('cvFile', e.target.files[0]);
    const token = localStorage.getItem('skillmatch_token');
    
    try {
      const res = await fetch(`${apiBaseUrl}/api/ai/review-cv`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      const data = await res.json();
      if (res.status === 403 && data.requireUpgrade) { setUpgradeMessage(data.message); setShowUpgradeModal(true); return; }
      if (!res.ok) throw new Error(data.message);
      
      // Update usage locally sau khi upload thành công
      setCvUsage(prev => ({ ...prev, used: prev.used + 1 }));
      
      navigate('/candidate/cv-review-result', { state: { result: data } });
    } catch (err) { alert("Lỗi: " + err.message); } finally { setIsUploading(false); e.target.value = null; }
  };

  if (loading) return <div className="h-screen bg-white"></div>;

  return (
    <div className="homepage-wrapper">
      <main className="main-content">
        
        {/* HERO SECTION */}
        <section className="hero-section">
          {!isSearching && (
            <div className="badge">
                <span className="dot-ping"></span>
                <span>{userRole === 'business' ? 'Hệ thống đánh giá năng lực AI' : 'Hơn 500+ công việc mới hôm nay'}</span>
            </div>
          )}

          <h1 className="hero-title">
            {userRole === 'business' ? (
                <>Tuyển dụng nhân tài <br /> <span style={{ color: '#2563EB' }}>chính xác nhất.</span></>
            ) : (
                <>Tuyển dụng không <br /> <span style={{ color: '#2563EB' }}>cảm tính.</span></>
            )}
          </h1>

          <form onSubmit={handleSearch} className="modern-search-bar">
            <div className="search-group flex-grow">
              <span className="search-icon"><IconSearch /></span>
              <input type="text" placeholder={userRole === 'business' ? "Tìm kiếm ứng viên..." : "Vị trí tuyển dụng..."} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
            </div>
            <div className="search-divider"></div>
            <div className="search-group location-group">
              <span className="search-icon"><IconMapPin /></span>
              <select value={location} onChange={(e) => setLocation(e.target.value)}>
                <option value="">Tất cả địa điểm</option>
                {provinces.map(prov => (
                  <option key={prov.code} value={prov.name}>{prov.name.replace('Thành phố ', '').replace('Tỉnh ', '')}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-search-modern">{userRole === 'business' ? 'Tìm ứng viên' : 'Tìm việc làm'}</button>
          </form>
        </section>

        {/* CONTENT SECTIONS */}
        {userRole === 'business' ? (
            /* GIAO DIỆN BUSINESS */
            <section className="bento-container">
                <div className="section-header">
                    <h2 className="section-title">Quản lý tuyển dụng</h2>
                    <Link to="/business/dashboard" className="link-view-all">Vào Dashboard <IconArrowRight /></Link>
                </div>

                <div className="bento-grid">
                    {/* CARD 1: ĐĂNG TIN (Main Action) */}
                    <div className="bento-card card-featured" onClick={() => navigate('/business/jobs/new')} style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)', color: 'white' }}>
                        <div className="hot-badge" style={{background: '#F59E0B', color: 'white'}}>⚡️ Action</div>
                        <div style={{marginTop: 'auto'}}>
                            <h3 style={{fontSize: '1.8rem', fontWeight: 800, marginBottom: 8}}>Đăng tin tuyển dụng</h3>
                            <p style={{opacity: 0.9, fontSize: '1rem', marginBottom: 20}}>Tiếp cận hàng nghìn ứng viên tiềm năng ngay hôm nay.</p>
                            <button className="btn-apply" style={{background: 'white', color: '#2563EB'}}>Tạo Job Mới</button>
                        </div>
                    </div>

                    {/* CARD 2: STATS */}
                    <div className="bento-card" onClick={() => navigate('/business/candidates')} style={{ cursor: 'pointer', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: '#F0FDFA' }}>
                        <div style={{ width: 56, height: 56, background: '#CCFBF1', color: '#0D9488', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, fontSize: '28px' }}>👥</div>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: '#115E59' }}>Tìm Ứng Viên</h3>
                        <p style={{ fontSize: '0.9rem', color: '#0F766E', margin: '8px 0 0' }}>Duyệt hồ sơ và <br/> sàng lọc bằng AI</p>
                    </div>

                    {/* CARD 3: DANH SÁCH JOB CỦA TÔI */}
                    <div className="bento-card card-companies" style={{gridColumn: 'span 2'}}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, width: '100%' }}>
                            <h3 style={{ fontWeight: 700, margin: 0, fontSize: '1.25rem' }}>Tin đang hiển thị</h3>
                            <Link to="/business/jobs" style={{color: '#2563EB', fontSize: '0.9rem', textDecoration: 'none'}}>Quản lý</Link>
                        </div>
                        <div className="company-list-wrapper">
                            {myJobs.length > 0 ? myJobs.map((job) => (
                                <div key={job._id} className="company-list-item" onClick={() => navigate(`/business/jobs/edit/${job._id}`)} style={{cursor: 'pointer'}}>
                                    <div className="c-logo-sm" style={{background: '#EFF6FF', color: '#2563EB'}}><IconBriefcase /></div>
                                    <div className="c-info">
                                        <p className="c-name">{job.title}</p>
                                        <p className="c-pos" style={{ color: '#059669', fontWeight: 600 }}>{job.candidateCount || 0} ứng viên</p>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center text-gray-400 text-sm py-4">Chưa có tin tuyển dụng nào.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        ) : (
            /* GIAO DIỆN CANDIDATE / GUEST */
            <>
                {/* 1. SLIDER BANNER & LIVE JOB FEED */}
                <div className="max-w-7xl mx-auto px-4 -mt-10 relative z-20 mb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* BANNER SLIDER */}
                        <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden h-[340px] relative group border border-gray-100">
                            <div className="w-full h-full relative">
                                {banners.map((banner, index) => (
                                    <div key={index} className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                                        
                                        {/* Ảnh nền */}
                                        <img src={banner.image} alt={banner.company} className="w-full h-full object-cover"/>
                                        
                                        {/* Gradient Overlay */}
                                        <div className={`absolute inset-0 bg-gradient-to-t ${banner.color} via-transparent to-transparent opacity-90`}></div>
                                        
                                        {/* Nội dung Banner */}
                                        <div className="absolute inset-0 flex flex-col justify-end p-8 text-white">
                                            <div className="flex items-center gap-2 mb-2 animate-slideUp">
                                                <span className="bg-yellow-400 text-blue-900 text-[10px] font-extrabold px-2 py-1 rounded uppercase tracking-wider">Đối tác Chiến lược</span>
                                                <span className="bg-white/20 backdrop-blur-md px-2 py-1 rounded text-[10px] font-bold border border-white/30 flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[12px]">verified</span> 
                                                    {banner.company}
                                                </span>
                                            </div>
                                            
                                            <h2 className="text-3xl font-bold mb-2 animate-slideUp delay-100 line-clamp-1">{banner.company}</h2>
                                            <p className="text-gray-200 mb-6 text-sm max-w-lg animate-slideUp delay-200 line-clamp-2">{banner.desc}</p>
                                            
                                            <button 
                                                onClick={() => navigate(banner.link)}
                                                className="bg-white text-blue-900 px-6 py-2.5 rounded-lg font-bold w-fit hover:bg-blue-50 transition-all shadow-lg animate-slideUp delay-300 flex items-center gap-2"
                                            >
                                                Xem hồ sơ công ty <IconArrowRight />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Dots */}
                            {banners.length > 1 && (
                                <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                                    {banners.map((_, idx) => (
                                        <div key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2 h-2 rounded-full cursor-pointer transition-all ${currentSlide === idx ? 'bg-white w-6' : 'bg-white/50'}`}></div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* LIVE JOB FEED */}
                        <div className="bg-white rounded-2xl shadow-lg p-0 overflow-hidden h-[340px] flex flex-col border border-orange-100">
                            <div className="p-4 border-b border-orange-100 bg-orange-50 flex justify-between items-center">
                                <h3 className="font-bold text-orange-600 flex items-center gap-2 text-sm uppercase">🔥 Việc làm Hot nhất</h3>
                                <span className="text-[10px] bg-white text-orange-600 px-2 py-0.5 rounded-full border border-orange-200 animate-pulse">Live</span>
                            </div>
                            <div ref={scrollRef} className="flex-1 overflow-hidden relative p-3 space-y-3">
                                {[...hotJobs, ...hotJobs].map((job, idx) => (
                                    <Link to={`/jobs/${job._id}`} key={`${job._id}-${idx}`} className="block bg-white border border-gray-100 p-3 rounded-xl shadow-sm hover:shadow-md hover:border-orange-300 transition-all flex items-center gap-3 group">
                                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center font-bold text-gray-500 text-sm shrink-0 border border-gray-200">
                                            {job.companyName ? job.companyName.charAt(0).toUpperCase() : 'C'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-bold text-gray-800 text-sm truncate group-hover:text-orange-600">{job.title}</h4>
                                            <p className="text-xs text-gray-500 truncate">{job.companyName} • <span className="text-green-600 font-bold">{job.salary}</span></p>
                                        </div>
                                        {job.isHot && <HotBadge />}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. TOP COMPANIES & AI CV */}
                <div className="max-w-7xl mx-auto px-4 pb-20">
                    <div className="flex flex-col md:flex-row gap-8">
                        
                        {/* LEFT: TOP COMPANIES */}
                        <div className="flex-1">
                            <div className="flex justify-between items-end mb-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-900">Top Công ty hàng đầu</h2>
                                    <p className="text-gray-500 text-sm mt-1">Đối tác chiến lược & Doanh nghiệp Enterprise</p>
                                </div>
                                <Link to="/companies" className="text-blue-600 text-sm font-semibold hover:underline">Xem tất cả</Link>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {topCompanies.map((comp) => (
                                    // ✅ SỬA ĐƯỜNG DẪN LINK ĐỂ GUEST CŨNG XEM ĐƯỢC
                                    <Link to={`/companies/${comp._id}`} key={comp._id} className={`bg-white p-4 rounded-xl border transition-all flex items-center gap-4 relative overflow-hidden group ${comp.isTop ? 'border-yellow-300 shadow-sm hover:shadow-yellow-100' : 'border-gray-200 hover:shadow-md'}`}>
                                        {comp.isTop && <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-bl from-yellow-100 to-transparent rounded-bl-full -mr-6 -mt-6"></div>}
                                        <div className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100 text-xl font-bold text-gray-400">
                                            {comp.logo ? <img src={comp.logo} alt="" className="w-full h-full object-contain rounded-lg"/> : comp.name.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-1">
                                                {comp.name} 
                                                {comp.isTop && <span className="material-symbols-outlined text-blue-500 text-[14px]" title="Enterprise">verified</span>}
                                            </h3>
                                            <p className="text-xs text-gray-500 mt-0.5">{comp.jobCount} việc làm đang tuyển</p>
                                            {comp.isTop && <div className="mt-1"><TopBadge /></div>}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT: AI CV REVIEW CARD */}
                        <div className="md:w-1/3">
                            <div className="bg-gradient-to-br from-indigo-900 to-blue-900 rounded-2xl p-8 text-white shadow-xl h-full flex flex-col justify-center relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><span className="material-symbols-outlined text-9xl">psychology</span></div>
                                <div className="relative z-10 text-center">
                                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm mx-auto shadow-inner border border-white/10">
                                        <span className="material-symbols-outlined text-yellow-300 text-3xl">auto_awesome</span>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-3">AI CV Review</h3>
                                    <p className="text-blue-100 mb-6 text-sm leading-relaxed">
                                        Tối ưu hóa CV với công nghệ AI. Tăng 80% cơ hội trúng tuyển.
                                    </p>
                                    
                                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf" onChange={handleCvUpload} />
                                    
                                    <button 
                                        onClick={handleAIClick} 
                                        disabled={isUploading}
                                        className="block w-full py-3 bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold text-center rounded-xl shadow-lg transition-transform hover:scale-105"
                                    >
                                        {isUploading ? 'Đang phân tích...' : 'Review CV Ngay'}
                                    </button>

                                    {/* ✅ HIỂN THỊ LƯỢT DÙNG */}
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                        {isLoggedIn ? (
                                            <div className="text-center">
                                                <div className="flex justify-between text-xs text-blue-200 mb-1">
                                                    <span>Đã dùng</span>
                                                    <span>{cvUsage.used}/{cvUsage.limit} lượt</span>
                                                </div>
                                                <div className="w-full bg-blue-950/50 rounded-full h-1.5 overflow-hidden">
                                                    <div 
                                                        className={`h-full rounded-full transition-all duration-500 ${cvUsage.used >= cvUsage.limit ? 'bg-red-500' : 'bg-green-400'}`} 
                                                        style={{ width: `${Math.min((cvUsage.used / cvUsage.limit) * 100, 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-center text-xs text-white/50">Miễn phí 2 lượt/tháng (Cần đăng nhập)</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )}
      </main>

      <style>{`
        /* Styles giữ nguyên từ code cũ của bạn */
        .bento-container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .modern-search-bar {
            background: white; border-radius: 50px; box-shadow: 0 10px 40px -10px rgba(37, 99, 235, 0.25);
            padding: 8px; display: flex; align-items: center; max-width: 700px; margin: 0 auto;
            border: 1px solid rgba(255, 255, 255, 0.5); position: relative; z-index: 10;
        }
        .search-group { display: flex; align-items: center; padding: 0 16px; position: relative; }
        .search-group.flex-grow { flex: 1; }
        .search-group input { width: 100%; border: none; outline: none; font-size: 15px; color: #111827; padding: 12px 0; background: transparent; }
        .search-divider { width: 1px; height: 24px; background: #E5E7EB; margin: 0 8px; }
        .location-group select { border: none; outline: none; background: transparent; font-size: 14px; color: #374151; font-weight: 500; cursor: pointer; padding-right: 12px; min-width: 140px; }
        .btn-search-modern { background: #2563EB; color: white; border: none; padding: 12px 32px; border-radius: 40px; font-weight: 600; font-size: 15px; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3); }
        .btn-search-modern:hover { background: #1D4ED8; transform: translateY(-1px); }
        
        @media (max-width: 768px) {
            .modern-search-bar { flex-direction: column; border-radius: 20px; padding: 16px; gap: 12px; }
            .search-divider { display: none; }
            .search-group, .location-group, .btn-search-modern { width: 100%; }
            .search-group input { border-bottom: 1px solid #eee; }
        }
            .animate-slideUp { animation: slideUp 0.6s ease-out forwards; opacity: 0; transform: translateY(20px); }
        @keyframes slideUp { to { opacity: 1; transform: translateY(0); } }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
      `}</style>
      
      <UpgradeRequestModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Hết lượt Review miễn phí"
        message={upgradeMessage}
        targetPlan="Pro"
      />
    </div>
  );
}